// Persists the moment an async (online) visit's claim window started, keyed by VisitId, so the
// "Are you still there?" countdown survives a page refresh (there is no server-provided claim
// timestamp). The backend independently enforces the real release; this is only the client-side
// warning anchor. Entries are cleared on explicit close-out (defer/complete/expiry/failed-reclaim)
// — never on unmount — and stale entries are pruned on write as a safety net against orphans.

const STORAGE_KEY = 'asyncClaimStart'
// A claim window is minutes long; anything older than a day is an orphan from a close path that
// never ran (hard crash / closed tab), so drop it rather than let the map grow unbounded.
const MAX_AGE_MS = 24 * 60 * 60 * 1000

type ClaimStartMap = Record<string, number>

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

function readMap(): ClaimStartMap {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return {}
    const parsed: unknown = JSON.parse(raw)
    if (!isRecord(parsed)) return {}
    const result: ClaimStartMap = {}
    for (const [visitId, value] of Object.entries(parsed)) {
      if (typeof value === 'number') result[visitId] = value
    }
    return result
  } catch {
    return {}
  }
}

function writeMap(map: ClaimStartMap): void {
  const now = Date.now()
  const pruned: ClaimStartMap = {}
  for (const [visitId, ts] of Object.entries(map)) {
    if (now - ts < MAX_AGE_MS) pruned[visitId] = ts
  }
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(pruned))
  } catch {
    // localStorage unavailable (private mode / quota). Best-effort only — the backend still
    // enforces the claim timeout, so a missing anchor just means no client-side warning.
  }
}

// Returns the stored claim-start for this visit, or initializes it to now (first claim / first
// mount after a refresh) and returns that.
export function getOrInitClaimStart(visitId: string): number {
  const map = readMap()
  const existing = map[visitId]
  if (typeof existing === 'number') return existing
  const now = Date.now()
  map[visitId] = now
  writeMap(map)
  return now
}

// Starts a fresh window (used after a successful "Continue visit" re-claim). Returns the new anchor.
export function resetClaimStart(visitId: string): number {
  const map = readMap()
  const now = Date.now()
  map[visitId] = now
  writeMap(map)
  return now
}

export function clearClaimStart(visitId: string): void {
  const map = readMap()
  if (!(visitId in map)) return
  delete map[visitId]
  writeMap(map)
}
